import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import KulkarniConsultingNote from './KulkarniConsultingNote';
import PricingPage from './PricingPage';
import { APP_LOGO_PNG } from '../branding';
import { API_BASE } from '../api/client';
import { isSupabasePasswordPolicyError, passwordMeetsPolicy } from '../utils/passwordPolicy';

export default function Login() {
  const { t } = useTranslation();
  const {
    login,
    signup,
    setDevUser,
    supabaseConfigured,
    passwordRecoveryInProgress,
    requestPasswordReset,
    completePasswordReset,
  } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'forgotPassword' | 'pricing'
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';

  const notifySignup = async (signupEmail, signupFullName, signupPhone) => {
    try {
      await fetch(`${API_BASE}/api/notify-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: signupEmail,
          full_name: signupFullName || undefined,
          phone: signupPhone || undefined,
        }),
      });
    } catch (err) {
      console.warn('notify-signup failed:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email.trim(), password);
      } else {
        if (!passwordMeetsPolicy(password)) {
          setError(t('auth.passwordPolicyRequirements'));
          return;
        }
        const trimmedEmail = email.trim();
        const trimmedName = fullName.trim();
        const trimmedPhone = phone.trim();
        await signup(trimmedEmail, password, {
          full_name: trimmedName || undefined,
          phone: trimmedPhone || undefined,
        });
        await notifySignup(trimmedEmail, trimmedName, trimmedPhone);
        setMessage(t('auth.confirmEmailSent'));
      }
    } catch (err) {
      if (mode === 'signup' && isSupabasePasswordPolicyError(err)) {
        setError(t('auth.passwordPolicyRequirements'));
      } else {
        setError(err.message || (mode === 'login' ? t('auth.errorLogin') : t('auth.errorSignup')));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setMessage(t('auth.resetLinkSent'));
    } catch (err) {
      setError(err.message || t('auth.errorPasswordReset'));
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (newPassword !== confirmNewPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }
    if (!passwordMeetsPolicy(newPassword)) {
      setError(t('auth.passwordPolicyRequirements'));
      return;
    }
    setLoading(true);
    try {
      await completePasswordReset(newPassword);
    } catch (err) {
      setError(
        isSupabasePasswordPolicyError(err)
          ? t('auth.passwordPolicyRequirements')
          : (err.message || t('auth.errorPasswordUpdate')),
      );
    } finally {
      setLoading(false);
    }
  };

  if (!supabaseConfigured) {
    return (
      <div className="app-shell-bg min-h-[100dvh] flex flex-col px-3 sm:px-4 w-full max-w-full min-w-0">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md rounded-2xl border border-emerald-200/80 bg-white/95 shadow-card backdrop-blur-sm p-6 sm:p-10">
            <div className="flex justify-center mb-6">
              <img
                src={APP_LOGO_PNG}
                alt=""
                className="h-24 sm:h-28 w-auto max-w-[min(100%,280px)] object-contain"
                decoding="async"
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-sans font-extrabold text-brand-green mb-3 tracking-tight leading-tight">
              {t('auth.title')}
            </h1>
            <p className="text-slate-600 text-base sm:text-lg font-medium leading-relaxed mb-6">{t('auth.subtitle')}</p>
            <p className="text-amber-800 text-base font-medium leading-relaxed mb-4">
              Configure REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to enable login.
            </p>
            {isLocalhost && setDevUser && (
              <button
                type="button"
                onClick={setDevUser}
                className="w-full py-3.5 px-4 rounded-xl border border-amber-300/80 bg-gradient-to-r from-amber-50 to-amber-100/80 text-amber-900 text-base font-bold hover:from-amber-100 hover:to-amber-50"
              >
                Continue without login (dev)
              </button>
            )}
          </div>
        </div>
        <KulkarniConsultingNote className="mx-[-0.75rem] sm:mx-[-1rem] mt-4" />
      </div>
    );
  }

  if (mode === 'pricing') {
    return <PricingPage variant="public" onBack={() => setMode('login')} />;
  }

  if (passwordRecoveryInProgress) {
    return (
      <div className="app-shell-bg min-h-[100dvh] flex flex-col px-3 sm:px-4 w-full max-w-full min-w-0">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md rounded-2xl border border-emerald-200/80 bg-white/95 shadow-card backdrop-blur-sm p-6 sm:p-10">
            <div className="flex justify-center mb-6">
              <img
                src={APP_LOGO_PNG}
                alt=""
                className="h-24 sm:h-28 w-auto max-w-[min(100%,280px)] object-contain"
                decoding="async"
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-sans font-extrabold text-brand-green mb-3 tracking-tight leading-tight">
              {t('auth.setNewPasswordTitle')}
            </h1>
            <p className="text-slate-600 text-base sm:text-lg font-medium leading-relaxed mb-8">
              {t('auth.setNewPasswordHint')}
            </p>
            <form onSubmit={handleRecoverySubmit} className="space-y-5">
              <div>
                <label htmlFor="recovery-new-password" className="block text-sm font-semibold text-slate-700 mb-1.5">
                  {t('auth.newPassword')}
                </label>
                <input
                  id="recovery-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3.5 text-base border border-emerald-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 bg-white/80"
                  required
                />
              </div>
              <div>
                <label htmlFor="recovery-confirm-password" className="block text-sm font-semibold text-slate-700 mb-1.5">
                  {t('auth.confirmNewPassword')}
                </label>
                <input
                  id="recovery-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="w-full px-4 py-3.5 text-base border border-emerald-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 bg-white/80"
                  required
                />
              </div>
              {error && (
                <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-base text-red-700 font-medium" role="alert">
                  {error}
                </div>
              )}
              {message && (
                <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-base text-green-800 font-medium" role="status">
                  {message}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-brand-emerald to-emerald-700 text-white font-bold text-base shadow-soft hover:from-emerald-700 hover:to-brand-emerald focus:outline-none focus:ring-2 focus:ring-amber-400/70 focus:ring-offset-2 disabled:opacity-50"
              >
                {loading ? t('auth.savingPassword') : t('auth.saveNewPassword')}
              </button>
            </form>
          </div>
        </div>
        <KulkarniConsultingNote className="mx-[-0.75rem] sm:mx-[-1rem] mt-4" />
      </div>
    );
  }

  return (
    <div className="app-shell-bg min-h-[100dvh] flex flex-col px-3 sm:px-4 w-full max-w-full min-w-0">
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-[1fr_minmax(380px,460px)] gap-6 lg:gap-10 items-center">
          <section className="rounded-2xl border border-emerald-100/80 bg-white/80 shadow-card backdrop-blur-sm p-6 sm:p-8 lg:p-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-extrabold text-brand-green tracking-tight leading-tight">
              {t('auth.marketingLine1')}
            </h2>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-extrabold text-brand-green/95 tracking-tight leading-tight mt-1">
              {t('auth.marketingLine2')}
              <br />
              {t('auth.marketingLine3')}
            </h3>
            <p className="text-slate-700 text-base sm:text-lg font-semibold leading-relaxed mt-6">
              {t('auth.marketingPremium')}
            </p>
            <p className="text-slate-600 text-base sm:text-lg font-medium leading-relaxed mt-6">
              {t('auth.marketingDocuments')}
            </p>
          </section>

          <div className="w-full rounded-2xl border border-emerald-200/80 bg-white/95 shadow-card backdrop-blur-sm p-6 sm:p-10">
            <div className="flex justify-center mb-6">
              <img
                src={APP_LOGO_PNG}
                alt=""
                className="h-24 sm:h-28 w-auto max-w-[min(100%,280px)] object-contain"
                decoding="async"
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-sans font-extrabold text-brand-green mb-3 tracking-tight leading-tight">
              {mode === 'forgotPassword' ? t('auth.forgotPasswordTitle') : t('auth.title')}
            </h1>
            <p className="text-slate-600 text-base sm:text-lg font-medium leading-relaxed mb-8">
              {mode === 'forgotPassword' ? t('auth.forgotPasswordHint') : t('auth.subtitle')}
            </p>

            {mode === 'forgotPassword' ? (
              <form onSubmit={handleForgotSubmit} className="space-y-5">
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    {t('auth.email')}
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3.5 text-base border border-emerald-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 bg-white/80"
                    required
                  />
                </div>
                {error && (
                  <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-base text-red-700 font-medium" role="alert">
                    {error}
                  </div>
                )}
                {message && (
                  <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-base text-green-800 font-medium" role="status">
                    {message}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-brand-emerald to-emerald-700 text-white font-bold text-base shadow-soft hover:from-emerald-700 hover:to-brand-emerald focus:outline-none focus:ring-2 focus:ring-amber-400/70 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? t('auth.sendingResetLink') : t('auth.sendResetLink')}
                </button>
                <p className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setError(null);
                      setMessage(null);
                    }}
                    className="text-brand-emerald font-semibold hover:underline hover:text-brand-gold text-base"
                  >
                    {t('auth.backToLogin')}
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {mode === 'signup' && (
                  <div>
                    <label htmlFor="login-fullname" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      {t('auth.fullName')}
                    </label>
                    <input
                      id="login-fullname"
                      type="text"
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full px-4 py-3.5 text-base border border-emerald-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 bg-white/80"
                      required
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="login-email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    {t('auth.email')}
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3.5 text-base border border-emerald-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 bg-white/80"
                    required
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <label htmlFor="login-password" className="block text-sm font-semibold text-slate-700">
                      {t('auth.password')}
                    </label>
                    {mode === 'login' && (
                      <button
                        type="button"
                        onClick={() => {
                          setMode('forgotPassword');
                          setError(null);
                          setMessage(null);
                          setPassword('');
                        }}
                        className="text-sm font-semibold text-brand-emerald hover:underline hover:text-brand-gold shrink-0"
                      >
                        {t('auth.forgotPassword')}
                      </button>
                    )}
                  </div>
                  <input
                    id="login-password"
                    type="password"
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3.5 text-base border border-emerald-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 bg-white/80"
                    required
                  />
                </div>
                {mode === 'signup' && (
                  <div>
                    <label htmlFor="login-phone" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      {t('auth.phone')} <span className="text-slate-400 font-normal">({t('auth.optional')})</span>
                    </label>
                    <input
                      id="login-phone"
                      type="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-4 py-3.5 text-base border border-emerald-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/40 bg-white/80"
                    />
                  </div>
                )}
                {error && (
                  <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-base text-red-700 font-medium" role="alert">
                    {error}
                  </div>
                )}
                {message && (
                  <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-base text-green-800 font-medium" role="status">
                    {message}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-brand-emerald to-emerald-700 text-white font-bold text-base shadow-soft hover:from-emerald-700 hover:to-brand-emerald focus:outline-none focus:ring-2 focus:ring-amber-400/70 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? (mode === 'login' ? t('auth.loggingIn') : t('auth.signingUp')) : (mode === 'login' ? t('auth.login') : t('auth.signup'))}
                </button>
              </form>
            )}

            {mode !== 'forgotPassword' && (
              <p className="mt-6 text-center text-base text-slate-600 font-medium">
                {mode === 'login' ? (
                  <>
                    {t('auth.noAccount')}{' '}
                    <button type="button" onClick={() => { setMode('signup'); setError(null); setMessage(null); setFullName(''); setPhone(''); }} className="text-brand-emerald font-semibold hover:underline hover:text-brand-gold">
                      {t('auth.signup')}
                    </button>
                  </>
                ) : (
                  <>
                    {t('auth.hasAccount')}{' '}
                    <button type="button" onClick={() => { setMode('login'); setError(null); setMessage(null); }} className="text-brand-emerald font-semibold hover:underline hover:text-brand-gold">
                      {t('auth.loginHere')}
                    </button>
                  </>
                )}
              </p>
            )}

            <p className="mt-3 text-center">
              <button
                type="button"
                onClick={() => { setMode('pricing'); setError(null); setMessage(null); }}
                className="text-sm font-semibold text-brand-emerald hover:underline hover:text-brand-gold"
              >
                {t('auth.viewPricing')}
              </button>
            </p>

            {isLocalhost && setDevUser && (
              <button
                type="button"
                onClick={setDevUser}
                className="w-full mt-4 py-3.5 px-4 rounded-xl border border-amber-300/80 bg-gradient-to-r from-amber-50 to-amber-100/80 text-amber-900 text-base font-bold hover:from-amber-100 hover:to-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
              >
                Continue without login (dev)
              </button>
            )}
          </div>
        </div>
      </div>
      <KulkarniConsultingNote className="mx-[-0.75rem] sm:mx-[-1rem] mt-4" />
    </div>
  );
}
